// no dangerouslySetInnerHTML — titles are user-controlled text (SEC-REQ-4)
//
// V2 source path: customer/midas/midasHistoryPageSlice.ts
// V1 source: none (greenfield — Phase 4)
// Strategy: scratch
// Verified no-MUI: yes (no JSX)
// Phase: 4
//
// Per Phase 4 spec, history-page state lives in its own slice (not folded into
// midasPageSlice) to keep the chat slice lean. Holds the paginated thread list
// + filters for the /midas-v2/history sub-page.
//
// Loaded thunks consume the existing `chatBotApi.getThreads` endpoint
// (`GET /v1/bot/chat/threads?limit=50&offset=N&machine_id=...`) — no new
// endpoints required. Page-size + offset cap are SEC-REQ guarded:
//   SEC-REQ-2: client-side ceiling at offset ≤ 5000 (= 100 pages × 50). When
//     reached, the page hides "Load more" and an [BOT-SIDE] follow-up is filed
//     for server-side enforcement.
//
// Server-side filter is by `machine_id` only. Text search is client-side
// (locked product decision; no `?q=` param exists on the bot endpoint).
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Thread, ThreadListApiItem } from "./midasPageModels";
import chatBotApi from "../../shared/chatBot/ChatBotApi";

// ============================================================
// Constants
// ============================================================

/**
 * Page size for the history list. Bumped 50 → 100 on 2026-05-04 after user
 * testing on `.cc` reported the list "feels capped at 100" — with the old
 * page size, two `Load more` clicks brought the visible set to exactly 100,
 * which felt like a hard cap. 100 is the API's max per request (`?limit=100`)
 * so we hit it in a single round-trip on the initial load and most users
 * will never need to click `Load more`. The slice's `hasMore` math + the
 * `HISTORY_OFFSET_CEILING` security guard still apply unchanged.
 */
export const HISTORY_PAGE_SIZE = 100;

/**
 * SEC-REQ-2 (P1): client-side cap on `offset` to prevent runaway pagination.
 * 5000 = 100 pages × 50. The "Load more" button hides once this ceiling is
 * reached. A `[BOT-SIDE]` follow-up question has been filed in QUESTIONS.md
 * asking for server-side enforcement of the same cap.
 */
export const HISTORY_OFFSET_CEILING = 5000;

/** Machine filter selection. */
export type HistoryMachineFilter = "all" | "platform" | string; // string = machine GUID

// ============================================================
// Helpers
// ============================================================

/** Same UTC-suffix normalization midasPageSlice uses for thread timestamps. */
const ensureUtcSuffix = (iso: string): string => {
  if (!iso) return iso;
  if (iso.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(iso)) return iso;
  return iso + "Z";
};

/** Map a raw API thread item to our Thread model.
 *  Phase 4.4b: `message_count` + `last_message_preview` are server-derived
 *  and propagated as-is — the server has already cleaned and truncated the
 *  preview. Never re-clean or re-truncate on the client. */
const mapApiThread = (item: ThreadListApiItem): Thread => ({
  id: item.id,
  title: item.title,
  machine_context: null,
  machine_id: item.machine_id ?? null,
  message_count: item.message_count ?? 0,
  last_message_preview: item.last_message_preview ?? null,
  last_message_at: ensureUtcSuffix(item.updated_at),
  created_at: ensureUtcSuffix(item.created_at),
});

/** Convert UI machine filter into the `machineId` arg the API expects. */
const filterToMachineIdArg = (
  filter: HistoryMachineFilter
): string | null | undefined => {
  if (filter === "all") return undefined; // no param
  if (filter === "platform") return null; // sends ?machine_id=null
  return filter; // GUID
};

// ============================================================
// State
// ============================================================

export interface MidasHistoryPageState {
  /** Loaded threads (accumulates across "Load more" calls). */
  threads: Thread[];
  /** Total count across all pages — for the CURRENTLY-active filter (from API).
   *  Drives the inline `· N` counter in the page header AND the `Load more`
   *  pagination math (`state.threads.length < total`). When the user clicks
   *  a chip filter (e.g. "Platform") this drops to the filtered subset count
   *  by design, so the page header reflects "you are viewing N filtered
   *  threads". The sidebar History tab does NOT read this — it reads
   *  `globalTotal` so the global count stays stable across filter changes. */
  total: number;
  /** Total count across all pages with NO filter applied — the canonical
   *  global count for the current user. Updated only when a fetch was made
   *  with `machineFilter === "all"`. Survives `resetHistoryPage` (page
   *  unmount) so the sidebar count doesn't blink to 0 when the user leaves
   *  /midas-v2/history. The MidasV2Layout mount-probe (Phase 4.6) and
   *  in-page initial load both fire with `machineFilter: "all"` so this
   *  field gets populated reliably. Sub-phase 4.6-fix (2026-05-04). */
  globalTotal: number;
  /** Next offset to request — incremented after each fetch by HISTORY_PAGE_SIZE. */
  offset: number;
  /** Whether more pages are available (offset < total AND not at ceiling). */
  hasMore: boolean;
  /** Initial page load in flight. */
  loading: boolean;
  /** "Load more" in flight. */
  loadingMore: boolean;
  /** Error message (null when no error). */
  error: string | null;
  /** Server-side machine filter selection. */
  machineFilter: HistoryMachineFilter;
  /** Client-side text search query (filters loaded threads by title). */
  searchQuery: string;
  /**
   * Sticky set of machine IDs the user has chats with — accumulated across
   * every successful fetch on this page. Used to render the chip row so the
   * chips don't disappear when the active filter (e.g. "Platform") returns
   * threads that don't reference any machine. Reset only on
   * `resetHistoryPage` (page unmount).
   */
  seenMachineIds: string[];
}

const initialState: MidasHistoryPageState = {
  threads: [],
  total: 0,
  globalTotal: 0,
  offset: 0,
  hasMore: false,
  loading: false,
  loadingMore: false,
  error: null,
  machineFilter: "all",
  searchQuery: "",
  seenMachineIds: [],
};

/** Merge new machine IDs from a thread batch into the sticky seen set. */
const mergeSeenMachineIds = (
  existing: string[],
  threads: Thread[]
): string[] => {
  const set = new Set(existing);
  let changed = false;
  threads.forEach((t) => {
    if (t.machine_id && !set.has(t.machine_id)) {
      set.add(t.machine_id);
      changed = true;
    }
  });
  return changed ? Array.from(set) : existing;
};

// ============================================================
// Thunks
// ============================================================

/**
 * Initial fetch — replaces the loaded list. Used on page mount AND whenever the
 * machine filter changes (server-side filter requires a fresh fetch).
 */
export const fetchHistoryThreads = createAsyncThunk(
  "midasHistoryPage/fetchHistoryThreads",
  async (args: { machineFilter: HistoryMachineFilter }) => {
    const machineIdArg = filterToMachineIdArg(args.machineFilter);
    const raw = await chatBotApi.getThreads(HISTORY_PAGE_SIZE, 0, machineIdArg);
    return {
      threads: (raw.threads || []).map(mapApiThread),
      total: raw.total ?? 0,
    };
  }
);

/** Load the next page — appends to the list. */
export const loadMoreHistoryThreads = createAsyncThunk<
  { threads: Thread[]; total: number; nextOffset: number },
  void,
  { state: { midasHistoryPage: MidasHistoryPageState } }
>(
  "midasHistoryPage/loadMoreHistoryThreads",
  async (_arg, { getState }) => {
    const state = getState().midasHistoryPage;
    const nextOffset = state.offset + HISTORY_PAGE_SIZE;
    // SEC-REQ-2: Client-side offset ceiling. Belt-and-braces guard — UI also
    // hides "Load more" when offset reaches this value, but if a stale
    // dispatch slips through we refuse to send the request.
    if (nextOffset > HISTORY_OFFSET_CEILING) {
      return { threads: [], total: state.total, nextOffset: state.offset };
    }
    const machineIdArg = filterToMachineIdArg(state.machineFilter);
    const raw = await chatBotApi.getThreads(
      HISTORY_PAGE_SIZE,
      nextOffset,
      machineIdArg
    );
    return {
      threads: (raw.threads || []).map(mapApiThread),
      total: raw.total ?? 0,
      nextOffset,
    };
  }
);

// ============================================================
// Slice
// ============================================================

const midasHistoryPageSlice = createSlice({
  name: "midasHistoryPage",
  initialState,
  reducers: {
    /** Update the machine filter. Caller is responsible for re-fetching.
     *  NOTE: We deliberately do NOT clear `seenMachineIds` — those chips
     *  must remain visible across filter changes, so the user can switch
     *  back to "All" or pick a different machine without losing context.
     *
     *  NOTE 2 (Apr 30 visual-polish fix): We also deliberately do NOT wipe
     *  `state.threads`, `state.total`, `state.offset`, or `state.hasMore`
     *  here. Wiping them synchronously on chip click caused the page body
     *  to flash through `MidasHistoryEmptyV2` ("No conversations yet")
     *  during the same render tick, then through `MidasHistorySkeletonV2`
     *  on the .pending tick, before the real rows arrived ~200ms later —
     *  three height changes in <500ms produced the user-visible "letters
     *  jump as if the whole page is loading again". By keeping the stale
     *  thread list during the fetch, the body stays put with the previous
     *  rows; `.fulfilled` then replaces them atomically (and resets
     *  offset/hasMore correctly there). This is the standard pattern in
     *  Linear/Notion/Slack — re-filter swaps rows, never empties+reloads.
     *  Error is still cleared in `fetchHistoryThreads.pending`, so a stale
     *  "Couldn't load" doesn't bleed into the new request.
     */
    setMachineFilter: (state, action: PayloadAction<HistoryMachineFilter>) => {
      state.machineFilter = action.payload;
    },
    /** Update the client-side search query. */
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    /** Reset slice to initial state — used on page unmount or hard refresh.
     *  PRESERVES `globalTotal` so the sidebar History tab count doesn't
     *  blink to 0 when the user navigates away from /midas-v2/history.
     *  The next unfiltered fetch (mount probe in MidasV2Layout, or the
     *  page's own initial load) will refresh it. Sub-phase 4.6-fix. */
    resetHistoryPage: (state) => ({
      ...initialState,
      globalTotal: state.globalTotal,
    }),
  },
  extraReducers: (builder) => {
    builder
      // ── fetchHistoryThreads (initial / filter change) ──
      .addCase(fetchHistoryThreads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHistoryThreads.fulfilled, (state, action) => {
        const { payload } = action;
        state.threads = payload.threads;
        state.total = payload.total;
        // Sub-phase 4.6-fix (2026-05-04): only refresh `globalTotal` when this
        // fetch was unfiltered — `machineFilter === "all"`. A filtered fetch
        // (Platform / specific machine) returns a smaller subset count which
        // must NOT overwrite the global count the sidebar reads.
        if (action.meta.arg.machineFilter === "all") {
          state.globalTotal = payload.total;
        }
        state.offset = 0;
        // hasMore: more rows exist on server AND we haven't hit the ceiling
        state.hasMore =
          payload.threads.length === HISTORY_PAGE_SIZE &&
          payload.threads.length < payload.total &&
          HISTORY_PAGE_SIZE < HISTORY_OFFSET_CEILING;
        state.loading = false;
        state.error = null;
        // Sticky chip set — survives across filter changes (Bug fix:
        // clicking "Platform" used to wipe machine chips because the new
        // thread set had no machine_ids).
        state.seenMachineIds = mergeSeenMachineIds(
          state.seenMachineIds,
          payload.threads
        );
      })
      .addCase(fetchHistoryThreads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to load history";
      })
      // ── loadMoreHistoryThreads ──
      .addCase(loadMoreHistoryThreads.pending, (state) => {
        state.loadingMore = true;
        state.error = null;
      })
      .addCase(loadMoreHistoryThreads.fulfilled, (state, { payload }) => {
        // Defensive: avoid accidental duplicates if the API returns overlap
        const existing = new Set(state.threads.map((t) => t.id));
        const newThreads = payload.threads.filter((t) => !existing.has(t.id));
        state.threads = state.threads.concat(newThreads);
        state.total = payload.total;
        // Sub-phase 4.6-fix: only refresh `globalTotal` if the active filter
        // is "all" (the load-more thunk uses the slice's current filter).
        if (state.machineFilter === "all") {
          state.globalTotal = payload.total;
        }
        state.offset = payload.nextOffset;
        const reachedCeiling = payload.nextOffset >= HISTORY_OFFSET_CEILING;
        state.hasMore =
          !reachedCeiling &&
          payload.threads.length === HISTORY_PAGE_SIZE &&
          state.threads.length < payload.total;
        state.loadingMore = false;
        state.seenMachineIds = mergeSeenMachineIds(
          state.seenMachineIds,
          payload.threads
        );
      })
      .addCase(loadMoreHistoryThreads.rejected, (state, action) => {
        state.loadingMore = false;
        state.error = action.error.message || "Failed to load more";
      });
  },
});

export const { setMachineFilter, setSearchQuery, resetHistoryPage } =
  midasHistoryPageSlice.actions;
export default midasHistoryPageSlice.reducer;
